export function usePortalPassPricing(
  sports: Ref<Sport[] | undefined>,
  existingSports?: Ref<Sport[] | undefined>
) {
  const prices = computed(() => {
    // Determine if we have 2 sports selected
    const hasTwoSports = sports.value?.length === 2;

    // Check if we're adding a second sport to an existing portal pass
    const isAddingSecondSport =
      existingSports?.value &&
      existingSports.value.length === 1 &&
      sports.value?.length === 2 &&
      sports.value.some((sport) => existingSports.value?.includes(sport));

    // If adding a second sport to existing pass, use the add-on pricing
    if (isAddingSecondSport) {
      return {
        // For add-on pricing, we use the discounted rates:
        // 1 season add-on: $1000
        // 2 season add-on: $1750
        // 3 season add-on: $2250
        single: 1000,
        double: 1750,
        triple: 2250,

        // Full prices (for strikethrough display)
        fullSingle: 2000,
        fullDouble: 3750,
        fullTriple: 5500,
      };
    }

    // Regular pricing (same as before)
    return {
      single: hasTwoSports ? 3000 : 2000,
      double: hasTwoSports ? 5200 : 3750,
      triple: hasTwoSports ? 6600 : 5500,

      // Full prices (for strikethrough display)
      fullDouble: hasTwoSports ? 6000 : 4000,
      fullTriple: hasTwoSports ? 9000 : 6000,
    };
  });

  const savings = computed(() => {
    return {
      double: prices.value.fullDouble - prices.value.double,
      triple: prices.value.fullTriple - prices.value.triple,
    };
  });

  const couponCodes = computed(() => {
    const hasTwoSports = sports.value?.length === 2;

    // Check if we're adding a second sport to an existing portal pass
    const isAddingSecondSport =
      existingSports?.value &&
      existingSports.value.length === 1 &&
      sports.value?.length === 2 &&
      sports.value.some((sport) => existingSports.value?.includes(sport));

    // If adding a second sport, use the add-on coupon codes
    if (isAddingSecondSport) {
      return {
        single: 'stripeCouponOneYearOneSportAddOn',
        double: 'stripeCouponTwoYearOneSportAddOn',
        triple: 'stripeCouponThreeYearOneSportAddOn',
      };
    }

    // Regular coupon codes
    return {
      single: hasTwoSports ? 'rDbKHH2u' : '',
      double: hasTwoSports ? 'WnMXv8z8' : '',
      triple: hasTwoSports ? 'CLvKRoat' : '',
    };
  });

  const couponLabels = computed(() => {
    const hasTwoSports = sports.value?.length === 2;

    // Check if we're adding a second sport to an existing portal pass
    const isAddingSecondSport =
      existingSports?.value &&
      existingSports.value.length === 1 &&
      sports.value?.length === 2 &&
      sports.value.some((sport) => existingSports.value?.includes(sport));

    // If adding a second sport, use the add-on labels
    if (isAddingSecondSport) {
      return {
        single: 'Add-On Sport',
        double: '2 Season Add-On Sport',
        triple: '3 Season Add-On Sport',
      };
    }

    // Regular labels
    return {
      single: hasTwoSports ? '2 Sport' : '',
      double: hasTwoSports ? '2 Season 2 Sport' : '2 Season',
      triple: hasTwoSports ? '3 Season 2 Sport' : '3 Season',
    };
  });

  return {
    prices,
    savings,
    couponCodes,
    couponLabels,
  };
}
